import React, { useState, useEffect } from "react";
import { Button, Table, Row, Col, Image, Form, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { db, storage } from "../../firebase";
import defaultImage from "../../Assets/images/piLogo.png";
import styles from "./headshots.module.scss";

export const Headshots = () => {
  const [players, setPlayers] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]); // Store original sorted data
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlayers, setSelectedPlayers] = useState(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sortBy, setSortBy] = useState("name"); // 'name' or 'headshot'
  const [sortDirection, setSortDirection] = useState("asc"); // 'asc' or 'desc'

  useEffect(() => {
    fetchAllPlayers();
  }, []);

  const fetchAllPlayers = async () => {
    setIsLoading(true);
    try {
      const snapshot = await db.collection("player-profile").get();
      
      const playersData = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          docId: doc.id, // Firebase document ID
          playerId: data.ID || doc.id, // Player profile ID
          firstName: data.FirstName || "",
          lastName: data.LastName || "",
          fullName: `${data.FirstName || ""} ${data.LastName || ""}`.trim(),
          headshot: data.Headshot || "",
          team: data.Team || "",
        };
      });

      // Sort alphabetically by last name, then first name (default)
      playersData.sort((a, b) => {
        const lastNameCompare = (a.lastName || "").localeCompare(b.lastName || "");
        if (lastNameCompare !== 0) return lastNameCompare;
        return (a.firstName || "").localeCompare(b.firstName || "");
      });

      setAllPlayers(playersData);
      applySorting(playersData, sortBy, sortDirection);
      toast.success(`Loaded ${playersData.length} player profiles`);
    } catch (error) {
      console.error("Error fetching players:", error);
      toast.error(`Failed to load players: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectPlayer = (playerId) => {
    setSelectedPlayers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(playerId)) {
        newSet.delete(playerId);
      } else {
        newSet.add(playerId);
      }
      return newSet;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = new Set(players.map((p) => p.id));
      setSelectedPlayers(allIds);
    } else {
      setSelectedPlayers(new Set());
    }
  };

  const handleDeleteHeadshots = async () => {
    if (selectedPlayers.size === 0) {
      toast.warning("Please select at least one player");
      return;
    }

    setIsDeleting(true);
    try {
      const selectedArray = Array.from(selectedPlayers);
      let successCount = 0;
      let errorCount = 0;
      let storageDeleteCount = 0;
      let storageErrorCount = 0;

      // Delete headshot URLs and Storage files in batches
      for (const playerId of selectedArray) {
        try {
          const player = players.find((p) => p.id === playerId);
          if (!player) continue;

          // Delete the Storage file if it exists and is a Firebase Storage URL
          if (hasHeadshot(player.headshot)) {
            try {
              // Check if it's a Firebase Storage URL (contains firebasestorage.googleapis.com or the storage bucket)
              const isFirebaseStorageUrl = 
                player.headshot.includes("firebasestorage.googleapis.com") ||
                player.headshot.includes("firebase") ||
                player.headshot.includes("profile-headshots");

              if (isFirebaseStorageUrl) {
                try {
                  // Get reference from URL and delete
                  const storageRef = storage.refFromURL(player.headshot);
                  await storageRef.delete();
                  storageDeleteCount++;
                } catch (storageError) {
                  // If refFromURL fails, try to extract path from URL
                  console.warn(`Could not delete Storage file using refFromURL for ${player.fullName}:`, storageError);
                  
                  // Try alternative: extract filename from URL and delete by path
                  try {
                    const urlParts = player.headshot.split("/");
                    const fileName = urlParts[urlParts.length - 1].split("?")[0]; // Remove query params
                    if (fileName) {
                      const storageRef = storage.ref(`profile-headshots/${fileName}`);
                      await storageRef.delete();
                      storageDeleteCount++;
                    }
                  } catch (altError) {
                    console.warn(`Could not delete Storage file by path for ${player.fullName}:`, altError);
                    storageErrorCount++;
                    // Continue anyway - we'll still clear the database field
                  }
                }
              } else {
                // External URL or non-Firebase URL - skip Storage deletion
                console.log(`Skipping Storage deletion for ${player.fullName} - not a Firebase Storage URL`);
              }
            } catch (storageErr) {
              console.error(`Error deleting Storage file for ${player.fullName}:`, storageErr);
              storageErrorCount++;
              // Continue anyway - we'll still clear the database field
            }
          }

          // Update the document to remove/clear the Headshot field
          await db.collection("player-profile").doc(player.docId).update({
            Headshot: "",
          });

          successCount++;
        } catch (error) {
          console.error(`Error deleting headshot for player ${playerId}:`, error);
          errorCount++;
        }
      }

      // Show success/error messages
      const messages = [];
      if (successCount > 0) {
        messages.push(`Removed headshot URLs from ${successCount} player(s)`);
      }
      if (storageDeleteCount > 0) {
        messages.push(`Deleted ${storageDeleteCount} file(s) from Storage`);
      }
      if (errorCount > 0) {
        messages.push(`Failed to remove URLs from ${errorCount} player(s)`);
      }
      if (storageErrorCount > 0) {
        messages.push(`Failed to delete ${storageErrorCount} Storage file(s) (URLs still cleared)`);
      }

      if (messages.length > 0) {
        const hasErrors = errorCount > 0 || storageErrorCount > 0;
        if (hasErrors) {
          toast.warning(messages.join(". "), { autoClose: 5000 });
        } else {
          toast.success(messages.join(". "), { autoClose: 5000 });
        }
      }

      // Refresh the player list (will maintain current sort)
      await fetchAllPlayers();
      setSelectedPlayers(new Set());
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting headshots:", error);
      toast.error(`Delete failed: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const hasHeadshot = (headshot) => {
    return (
      headshot &&
      headshot.trim() !== "" &&
      headshot !== "null" &&
      headshot.trim().length > 0
    );
  };

  const getImageSrc = (headshot) => {
    return hasHeadshot(headshot) ? headshot : defaultImage;
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle direction if clicking the same column
      const newDirection = sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(newDirection);
      applySorting(allPlayers, column, newDirection);
    } else {
      // New column, default to ascending
      setSortBy(column);
      setSortDirection("asc");
      applySorting(allPlayers, column, "asc");
    }
  };

  const applySorting = (playersData, sortColumn, direction) => {
    const sorted = [...playersData];
    
    if (sortColumn === "headshot") {
      sorted.sort((a, b) => {
        const aHasHeadshot = hasHeadshot(a.headshot) ? 1 : 0;
        const bHasHeadshot = hasHeadshot(b.headshot) ? 1 : 0;
        
        if (direction === "asc") {
          // Ascending: players with headshots first
          return bHasHeadshot - aHasHeadshot;
        } else {
          // Descending: players without headshots first
          return aHasHeadshot - bHasHeadshot;
        }
      });
    } else {
      // Default: sort by name
      sorted.sort((a, b) => {
        const lastNameCompare = (a.lastName || "").localeCompare(b.lastName || "");
        if (lastNameCompare !== 0) {
          return direction === "asc" ? lastNameCompare : -lastNameCompare;
        }
        const firstNameCompare = (a.firstName || "").localeCompare(b.firstName || "");
        return direction === "asc" ? firstNameCompare : -firstNameCompare;
      });
    }
    
    setPlayers(sorted);
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <span className="text-muted">↕</span>;
    }
    return sortDirection === "asc" ? <span>↑</span> : <span>↓</span>;
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-2">Loading player profiles...</p>
        </div>
      </div>
    );
  }

  const selectedCount = selectedPlayers.size;
  const playersWithHeadshots = players.filter((p) => hasHeadshot(p.headshot));

  return (
    <div className={styles.headshotsContainer}>
      <Row className="mb-4">
        <Col>
          <h2>Player Headshots Management</h2>
          <p className="text-muted">
            View all players in alphabetical order. Select players to remove their headshot URLs
            and use the default image instead.
          </p>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <p>
            <strong>Total Players:</strong> {players.length} |{" "}
            <strong>With Headshots:</strong> {playersWithHeadshots.length} |{" "}
            <strong>Selected:</strong> {selectedCount}
          </p>
        </Col>
        <Col md={6} className="text-right">
          <Button
            variant="danger"
            onClick={() => setShowDeleteModal(true)}
            disabled={selectedCount === 0 || isDeleting}
          >
            Delete Headshot URLs ({selectedCount})
          </Button>
        </Col>
      </Row>

      <div className={styles.tableContainer}>
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th style={{ width: "50px" }}>
                <Form.Check
                  type="checkbox"
                  onChange={handleSelectAll}
                  checked={selectedPlayers.size === players.length && players.length > 0}
                />
              </th>
              <th style={{ width: "80px" }}>Image</th>
              <th 
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("name")}
              >
                Last Name {getSortIcon("name")}
              </th>
              <th>First Name</th>
              <th>Team</th>
              <th 
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("headshot")}
              >
                Has Headshot {getSortIcon("headshot")}
              </th>
            </tr>
          </thead>
          <tbody>
            {players.map((player) => {
              const isSelected = selectedPlayers.has(player.id);
              const hasImage = hasHeadshot(player.headshot);

              return (
                <tr
                  key={player.id}
                  className={isSelected ? styles.selectedRow : ""}
                >
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleSelectPlayer(player.id)}
                    />
                  </td>
                  <td>
                    <Image
                      src={getImageSrc(player.headshot)}
                      roundedCircle
                      style={{
                        width: "50px",
                        height: "50px",
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        e.target.src = defaultImage;
                      }}
                    />
                  </td>
                  <td>{player.lastName || "-"}</td>
                  <td>{player.firstName || "-"}</td>
                  <td>{player.team || "-"}</td>
                  <td>
                    {hasImage ? (
                      <span className="text-success">✓ Yes</span>
                    ) : (
                      <span className="text-muted">No (Default)</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete Headshot URLs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to remove the headshot URLs for{" "}
            <strong>{selectedCount}</strong> selected player(s)?
          </p>
          <p className="text-muted">
            This will clear the Headshot field for these players, causing them
            to use the default image. This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteHeadshots}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete Headshot URLs"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

